/**
 * RoleBasedRedirect
 *
 * Lleva al usuario a la primera pantalla de su rol. Es el elemento de la ruta
 * raíz (`/`) y de la ruta heredada `/dashboard`.
 *
 * ⚠️ **Aquí vivía un bucle de redirección infinito.** Un usuario autenticado
 * **sin perfil de staff** —un token de comercio que llega a esta SPA, o alguien
 * a quien le borraron la ficha con la sesión abierta— entraba en este ciclo:
 *
 * 1. `ProtectedRoute` de cualquier ruta con roles → sin rol → `/dashboard`.
 * 2. `/dashboard` → este componente → sin rol → `DEFAULT_DASHBOARD_ROUTE`,
 *    que es `/currencies`.
 * 3. `/currencies` es `SUPER_ADMIN` → `ProtectedRoute` → sin rol → `/dashboard`.
 * 4. Vuelta al paso 2, indefinidamente.
 *
 * La pestaña se quedaba girando sin decir nada. Ahora ese caso **no navega**:
 * enseña qué pasa y ofrece la salida.
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getDefaultRouteForRole } from '@/config/navigation';
import { Button } from '@/components/ui';
import type { StaffRole } from '@/types/auth.types';

export function RoleBasedRedirect() {
  const { user, signOut } = useAuth();

  const staffRole = user?.profiles?.staff?.role as StaffRole | undefined;

  if (!staffRole) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <div>
          <p className="text-lg font-semibold text-foreground">
            Tu cuenta no tiene acceso a este panel
          </p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Este dashboard es para personal de 121Pass. Si crees que es un
            error, contacta con tu responsable.
          </p>
        </div>
        <Button variant="outlined" onClick={() => void signOut()}>
          Cerrar sesión
        </Button>
      </div>
    );
  }

  const defaultRoute = getDefaultRouteForRole(staffRole);

  // Segunda guarda: si un rol se quedara sin ninguna entrada de menú,
  // `getDefaultRouteForRole` devuelve `'/'` y volveríamos aquí en bucle.
  if (defaultRoute === '/' || defaultRoute === '/dashboard') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <div>
          <p className="text-lg font-semibold text-foreground">
            Tu rol no tiene ninguna pantalla asignada
          </p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            El rol <span className="font-medium">{staffRole}</span> no tiene
            entradas de menú configuradas. Avisa al equipo de plataforma.
          </p>
        </div>
        <Button variant="outlined" onClick={() => void signOut()}>
          Cerrar sesión
        </Button>
      </div>
    );
  }

  return <Navigate to={defaultRoute} replace />;
}
